import { forwardRef, useRef } from "react";
import DatePicker from "react-datepicker";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import {
  formatLastAccessDay,
  getLastAccessDay,
  toExclusiveExpiry,
} from "../../../../shared/oversight-core/utils/get-org-user-info";
import styles from "./grant-expiry-picker.module.scss";

export type GrantExpiryVariant = "default" | "warning" | "danger" | "error";

interface IProps {
  // The exclusive ISO instant access stops at; the picker shows the day before.
  expiresAt?: string;
  onChange: (expiresAt?: string) => void;
  variant?: GrantExpiryVariant;
  disabled?: boolean;
  // False when the level needs an end date, which hides "Never expires".
  allowNever?: boolean;
}

interface IExpiryButtonProps {
  label: string;
  variant: GrantExpiryVariant;
  disabled?: boolean;
  onClick?: () => void;
}

const ExpiryButton = forwardRef<HTMLButtonElement, IExpiryButtonProps>(
  (props, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={props.onClick}
      disabled={props.disabled}
      className={`${styles.expiryButton} ${styles[props.variant]} font-size-13`}
    >
      <span>{props.label}</span>
      <MaterialIcon icon="calendar_month" size={16} />
    </button>
  ),
);

// The nearest ancestor that scrolls, which inside a dialog is the dialog's own box.
const findScrollParent = (element: HTMLElement | null) => {
  let node = element?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") {
      return node;
    }
    node = node.parentElement;
  }
  return undefined;
};

const GrantExpiryPicker = (props: IProps) => {
  const {
    expiresAt,
    onChange,
    variant = "default",
    disabled,
    allowNever = true,
  } = props;
  const datePickerRef = useRef<DatePicker>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);

  // The calendar renders outside the modal, so it is held inside the modal's box by hand: it opens
  // upward when there is no room below instead of running past the modal's bottom edge. It is
  // right-aligned with the button, which sits at the modal's right edge, so it never runs past that
  // side, and it opens to the left when neither above nor below has room (six-week months).
  const boundary = findScrollParent(anchorRef.current) ?? "clippingParents";

  const getLabel = () => {
    if (expiresAt) return formatLastAccessDay(expiresAt);
    return allowNever ? "Never" : "Pick a date";
  };

  return (
    <span ref={anchorRef} className="d-inline-block">
      <DatePicker
        ref={datePickerRef}
        selected={expiresAt ? getLastAccessDay(expiresAt).toDate() : null}
        onChange={(date: Date | null) =>
          onChange(date ? toExclusiveExpiry(date) : undefined)
        }
        minDate={new Date()}
        calendarStartDay={1}
        disabled={disabled}
        // The modal scrolls and clips its content, so the calendar renders outside it.
        portalId="grant-expiry-picker-portal"
        popperClassName={styles.popper}
        popperPlacement="bottom-end"
        popperModifiers={[
          {
            name: "flip",
            options: {
              boundary,
              fallbackPlacements: ["top-end", "left-end", "left-start"],
              padding: 8,
            },
          },
          {
            name: "preventOverflow",
            options: { boundary, padding: 8 },
          },
        ]}
        customInput={
          <ExpiryButton
            label={getLabel()}
            variant={!allowNever && !expiresAt ? "error" : variant}
            disabled={disabled}
          />
        }
      >
        {allowNever && (
          <div className="text-center pb-2">
            <span
              className="text-primary font-size-12 font-weight-500 cursor-pointer"
              onClick={() => {
                onChange(undefined);
                datePickerRef.current?.setOpen(false);
              }}
            >
              Never expires
            </span>
          </div>
        )}
      </DatePicker>
    </span>
  );
};

export default GrantExpiryPicker;
